import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Message } from '../../../types/chat.types';
import { FeedbackButtons } from '../FeedbackButtons/FeedbackButtons';
import {
  ChatMessage as ChatMessage__Root,
  ChatMessage__Bubble,
  ChatMessage__MarkdownWrapper,
  ChatMessage__Timestamp,
  ChatMessage__LoadingDots,
  ChatMessage__Dot,
  ChatMessage__Link,
} from './ChatMessage.styles';

interface ChatMessageProps {
  message: Message;
  onFeedback?: (messageId: string, traceId: string, isPositive: boolean, resetSelection: () => void) => void;
}


const LoadingDots: React.FC = () => {
  return (
    <ChatMessage__LoadingDots>
      <ChatMessage__Dot $delay="-0.32s" />
      <ChatMessage__Dot $delay="-0.16s" />
      <ChatMessage__Dot $delay="0s" />
    </ChatMessage__LoadingDots>
  );
};

const URL_REGEX = /(https?:\/\/[^\s]+)/g;

function renderWithLinks(text: string): React.ReactNode[] {
  const parts = text.split(URL_REGEX);
  return parts.map((part, i) =>
    /^https?:\/\//.test(part) ? (
      <ChatMessage__Link key={i} href={part} target="_blank" rel="noopener noreferrer">
        {part}
      </ChatMessage__Link>
    ) : (
      part
    ),
  );
}

export const ChatMessage: React.FC<ChatMessageProps> = ({ message, onFeedback }) => {
  const isUser = message.role === 'user';
  const isLoading = message.isLoading;
  const isAssistant = message.role === 'assistant';

  const formatTime = (timestamp: string) => {
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  return (
    <ChatMessage__Root $isUser={isUser}>
      <div>
        <ChatMessage__Bubble $isUser={isUser} $isLoading={!!isLoading}>
          {isLoading ? (
            <LoadingDots />
          ) : isUser ? (
            <span style={{ whiteSpace: 'pre-wrap' }}>{renderWithLinks(message.content)}</span>
          ) : (
            <ChatMessage__MarkdownWrapper>
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  a: ({ href, children }) => (
                    <ChatMessage__Link href={href} target="_blank" rel="noopener noreferrer">
                      {children}
                    </ChatMessage__Link>
                  ),
                }}
              >
                {message.content}
              </ReactMarkdown>
            </ChatMessage__MarkdownWrapper>
          )}
        </ChatMessage__Bubble>
        {!isLoading && (
          <ChatMessage__Timestamp $isUser={isUser}>{formatTime(message.timestamp)}</ChatMessage__Timestamp>
        )}
        {!isLoading && isAssistant && onFeedback && message.trace_id && (
          <FeedbackButtons
            messageId={message.id}
            traceId={message.trace_id}
            onFeedback={onFeedback}
          />
        )}
      </div>
    </ChatMessage__Root>
  );
};
